import type { MarketingLocale } from './marketing-locale'
import { MARKETING_PAGE_PAIRS } from './marketing-pages'

/** Canonical origin for absolute URLs in meta tags and JSON-LD. */
export const SITE_URL = 'https://planno.app'

/*
 * One card per language: the card carries the tagline and the feature list,
 * so a Portuguese card under an English page is the first thing a US reader
 * sees when someone shares `/en`. Rendered by `scripts/render-og-images.ts`.
 *
 * Bump `v` whenever either PNG changes. WhatsApp, Facebook, and LinkedIn cache
 * unfurl previews by exact URL (WhatsApp for up to ~30 days), so a changed
 * query string is the only reliable way to make them fetch the new card.
 * v=2: Poppins/Figtree rebrand. v=3: even glyph gap. v=4: per-locale cards.
 * (Aug 2026.)
 */
const OG_IMAGE_VERSION = 4

const OG_IMAGE_BY_LOCALE: Record<MarketingLocale, string> = {
  en: `${SITE_URL}/og-image-en.png?v=${OG_IMAGE_VERSION}`,
  'pt-BR': `${SITE_URL}/og-image.png?v=${OG_IMAGE_VERSION}`,
}

/** The share card for a locale — use this in JSON-LD so the structured data
 * and the `og:image` tag can never point at different cards. */
export const ogImageFor = (locale: MarketingLocale): string => OG_IMAGE_BY_LOCALE[locale]

const OG_IMAGE_ALT: Record<MarketingLocale, string> = {
  en: 'The Planno card — from brief to handover, all in one place',
  'pt-BR': 'Cartão do Planno — do briefing à entrega das chaves, reunido em um só lugar',
}

/** The og:image / twitter:image meta entries shared by every marketing route. */
export const ogImageMeta = (locale: MarketingLocale = 'pt-BR') => [
  { content: OG_IMAGE_BY_LOCALE[locale], property: 'og:image' },
  { content: '1200', property: 'og:image:width' },
  { content: '630', property: 'og:image:height' },
  { content: OG_IMAGE_ALT[locale], property: 'og:image:alt' },
  { content: OG_IMAGE_BY_LOCALE[locale], name: 'twitter:image' },
  { content: OG_IMAGE_ALT[locale], name: 'twitter:image:alt' },
]

/**
 * Reciprocal `hreflang` tags for a translated page, looked up from the single
 * pair list in `marketing-pages.ts`.
 *
 * Both URLs are emitted on BOTH pages of a pair — Google discards the whole
 * cluster when the annotations are one-directional. `x-default` points at the
 * Portuguese page: it is the canonical entry point for anyone whose language
 * we don't specifically serve.
 *
 * Throws for a path that isn't in the pair list, rather than silently
 * emitting nothing — a missing annotation is invisible in review and only
 * shows up weeks later as the wrong page ranking in the wrong market.
 */
export const alternateLinks = (path: string) => {
  const pair = MARKETING_PAGE_PAIRS.find(
    (candidate) => candidate['pt-BR'] === path || candidate.en === path
  )
  if (!pair) {
    throw new Error(
      `alternateLinks: "${path}" is not in MARKETING_PAGE_PAIRS. Add the pt-BR ↔ en pair there first.`
    )
  }
  return [
    { href: `${SITE_URL}${pair['pt-BR']}`, hrefLang: 'pt-BR', rel: 'alternate' },
    { href: `${SITE_URL}${pair.en}`, hrefLang: 'en', rel: 'alternate' },
    { href: `${SITE_URL}${pair['pt-BR']}`, hrefLang: 'x-default', rel: 'alternate' },
  ]
}

/**
 * The full head block for a translated marketing page: canonical, reciprocal
 * `hreflang`, Open Graph, and Twitter card. Every field that must stay in
 * sync across those four is derived from one `path` + `title` + `description`,
 * so a page cannot end up with an OG URL pointing somewhere its canonical
 * doesn't.
 *
 * The homepages don't use this — they carry extra JSON-LD and a font
 * preload, so they spell their head out.
 */
type PageHeadInput = {
  description: string
  locale: MarketingLocale
  path: string
  title: string
}

/** Everything except the language annotations, which differ by page kind. */
const pageMeta = ({ description, locale, path, title }: PageHeadInput) => [
  { title },
  { content: description, name: 'description' },
  { content: 'website', property: 'og:type' },
  { content: 'Planno', property: 'og:site_name' },
  { content: title, property: 'og:title' },
  { content: description, property: 'og:description' },
  { content: `${SITE_URL}${path}`, property: 'og:url' },
  ...ogImageMeta(locale),
  ...ogLocaleMeta(locale),
  { content: 'summary_large_image', name: 'twitter:card' },
  { content: title, name: 'twitter:title' },
  { content: description, name: 'twitter:description' },
]

export const marketingPageHead = (input: PageHeadInput) => ({
  links: [{ href: `${SITE_URL}${input.path}`, rel: 'canonical' }, ...alternateLinks(input.path)],
  meta: pageMeta(input),
})

/**
 * The head block for a page that exists in ONE language and has no counterpart
 * in the other — the help centre (#120), which is pt-BR only on purpose.
 *
 * Separate from `marketingPageHead` rather than an option on it, because the
 * difference is a claim about the page rather than a formatting preference.
 * `alternateLinks` throws for an unpaired path on purpose, and defeating that
 * guard with a placeholder pair would be worse than the error it prevents: an
 * `hreflang` pointing at an English page that does not exist tells Google to
 * serve a 404 to English readers. Omitting the annotation is the truthful
 * state, and this is where a page says so out loud.
 *
 * When the help centre gains English articles, these pages move to
 * `marketingPageHead` and get real pairs — that migration is the point of
 * keeping the two entry points visibly distinct.
 */
export const monolingualPageHead = (input: PageHeadInput) => ({
  links: [{ href: `${SITE_URL}${input.path}`, rel: 'canonical' }],
  meta: pageMeta(input),
})

/** `og:locale` plus the counterpart's `og:locale:alternate`. */
export const ogLocaleMeta = (locale: MarketingLocale) =>
  locale === 'en'
    ? [
        { content: 'en_US', property: 'og:locale' },
        { content: 'pt_BR', property: 'og:locale:alternate' },
      ]
    : [
        { content: 'pt_BR', property: 'og:locale' },
        { content: 'en_US', property: 'og:locale:alternate' },
      ]
